import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { Sparkle, PaintBrush, GridFour, UploadSimple, X, CheckCircle } from "@phosphor-icons/react";

const PRODUCTS = [
  { v:"business_card", l:"Business Cards", size:"3.5 × 2 in" },
  { v:"brochure", l:"Brochure", size:"A4 tri-fold" },
  { v:"flyer", l:"Flyer", size:"A5" },
  { v:"poster", l:"Poster", size:"A2" },
  { v:"packaging", l:"Packaging", size:"custom" },
  { v:"label", l:"Label", size:"custom" },
  { v:"hoarding", l:"Hoarding", size:"20 × 10 ft" },
];

const INDUSTRIES = ["FMCG","Pharma","D2C","Education","Real Estate","Banking","Restaurant","Retail","Tech","Fashion","Hospitality","Wellness","Other"];
const STYLES = ["modern","minimal","bold","luxury","playful","corporate","organic"];
const COLORS = ["vibrant","monochrome","pastel","dark","earth","neon"];

// Industry-specific extra fields
const INDUSTRY_FIELDS = {
  "Real Estate": [
    { k:"project_name", l:"Project name", ph:"e.g. Green Valley Homes" },
    { k:"price_range", l:"Price range", ph:"e.g. ₹80L – ₹1.5Cr" },
    { k:"sqft", l:"Configuration", ph:"e.g. 2/3 BHK · 1200-1800 sqft" },
    { k:"amenities", l:"Key amenities (comma-separated)", ph:"Pool, Gym, Clubhouse" },
    { k:"location", l:"Location", ph:"Sarjapur Road, Bangalore" },
  ],
  "Restaurant": [
    { k:"restaurant_name", l:"Restaurant name", ph:"e.g. Curry Leaf" },
    { k:"cuisine", l:"Cuisine type", ph:"South Indian · Vegetarian" },
    { k:"signature_dishes", l:"Signature dishes (comma-separated)", ph:"Dosa, Filter Coffee, Idiyappam" },
    { k:"price_range", l:"Price range", ph:"e.g. ₹200-500 per meal" },
    { k:"contact", l:"Phone / address", ph:"+91 98··· ··· · MG Road" },
  ],
  "Retail": [
    { k:"store_name", l:"Store name", ph:"e.g. Threadstore" },
    { k:"offer", l:"Offer / discount", ph:"Flat 40% OFF" },
    { k:"products", l:"Featured products", ph:"Shirts, Jeans, Accessories" },
    { k:"cta", l:"Call to action", ph:"Visit today · Free delivery" },
  ],
  "FMCG": [
    { k:"product_name", l:"Product name", ph:"e.g. Northwind Green Tea" },
    { k:"tagline", l:"Tagline", ph:"Sip. Slow. Smile." },
    { k:"variant", l:"Variant / SKU", ph:"25 tea bags · 50g" },
    { k:"ingredients", l:"Key ingredients", ph:"Assam CTC, Tulsi, Ginger" },
  ],
  "Pharma": [
    { k:"product_name", l:"Product name", ph:"e.g. VitaBoost D3" },
    { k:"composition", l:"Composition", ph:"Cholecalciferol 60000 IU" },
    { k:"dosage", l:"Dosage", ph:"1 capsule weekly" },
    { k:"disclaimer", l:"Regulatory line", ph:"Not for children under 12" },
  ],
  "Education": [
    { k:"institution", l:"Institution name", ph:"e.g. Sunrise Academy" },
    { k:"course", l:"Course / program", ph:"NEET Foundation · 2026" },
    { k:"admission_open", l:"Admission dates", ph:"Feb 15 – Mar 30, 2026" },
    { k:"contact", l:"Contact / website", ph:"+91 98··· ··· · sunrise.edu" },
  ],
  "Tech": [
    { k:"company", l:"Company", ph:"e.g. Northwind SaaS" },
    { k:"product", l:"Product / service", ph:"AI CRM for D2C" },
    { k:"tagline", l:"Tagline", ph:"Sales that write themselves." },
    { k:"website", l:"Website", ph:"northwind.co" },
  ],
  "Fashion": [
    { k:"brand", l:"Brand", ph:"e.g. Loomshire" },
    { k:"collection", l:"Collection", ph:"Spring/Summer 2026" },
    { k:"tagline", l:"Tagline", ph:"Handwoven. Handmade." },
  ],
  "Hospitality": [
    { k:"property_name", l:"Property name", ph:"e.g. Coorg Coffee Estate" },
    { k:"star_rating", l:"Star / category", ph:"5-star · Boutique · Homestay" },
    { k:"amenities", l:"Amenities", ph:"Pool, Spa, Restaurant, Wi-Fi" },
  ],
  "Wellness": [
    { k:"brand", l:"Brand", ph:"e.g. Kaya Ayurveda" },
    { k:"tagline", l:"Tagline", ph:"Rooted in tradition, made for today." },
    { k:"specialties", l:"Specialties", ph:"Skin, Hair, Immunity" },
  ],
};

export default function NewBrief() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const initialSource = ["ai", "template", "human"].includes(searchParams.get("source"))
    ? searchParams.get("source") : "ai";
  const [f, setF] = useState({
    title:"", product_type:"business_card", industry:"FMCG",
    size:"3.5 × 2 in", quantity:500, description:"",
    color_scheme:"vibrant", style:"modern",
    design_source:initialSource, template_id:null, logo_base64:null, extra_fields:{},
  });
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [logoPreview, setLogoPreview] = useState(null);

  // Prefill from Landing prompt seed
  useEffect(() => {
    const seed = localStorage.getItem("pf_prompt_seed");
    if (seed) {
      setF(prev => ({ ...prev, description: seed, title: seed.slice(0, 40) }));
      localStorage.removeItem("pf_prompt_seed");
    }
  }, []);

  // Load templates when product changes
  useEffect(() => {
    if (f.design_source === "template") {
      api.get(`/templates?product_type=${f.product_type}`).then(r => setTemplates(r.data));
    }
  }, [f.design_source, f.product_type]);

  const update = (k, v) => setF(prev => ({...prev, [k]:v}));
  const updateExtra = (k, v) => setF(prev => ({...prev, extra_fields: {...prev.extra_fields, [k]:v}}));

  const onLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2MB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setF(prev => ({...prev, logo_base64: reader.result.split(",")[1]}));
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (f.design_source === "template" && !f.template_id) {
      toast.error("Please pick a template"); return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/briefs", { ...f, quantity: Number(f.quantity) });
      const msg = f.design_source === "human" ? "Brief created — a designer will be assigned"
        : f.design_source === "template" ? "Brief created — our team will apply your template"
        : "Brief created";
      toast.success(msg);
      nav(`/brief/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    } finally { setLoading(false); }
  };

  const industryFields = INDUSTRY_FIELDS[f.industry] || [];

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]">
      <Nav />
      <div className="max-w-3xl mx-auto px-6 py-12">
        {(() => {
          // For template flow: 4 steps (source → industry → template → logo+desc)
          // For AI / human flow: 3 steps (source → industry → logo+desc; step 3 UI is skipped)
          const isTemplate = f.design_source === "template";
          const totalSteps = isTemplate ? 4 : 3;
          const displayStep = !isTemplate && step === 4 ? 3 : step;
          return <div className="pf-label mb-4">Step {displayStep} of {totalSteps}</div>;
        })()}
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-10">New brief.</h1>

        {step === 1 && (
          <div className="space-y-8" data-testid="brief-step-1">
            <div>
              <label className="pf-label block mb-3">Design source</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { v:"ai", i:Sparkle, t:"Generate with AI", d:"Free · ~30s", tone:"text" },
                  { v:"template", i:GridFour, t:"Start from template", d:"Pick a design, add logo", tone:"cyan" },
                  { v:"human", i:PaintBrush, t:"Hire a designer", d:"Print-ready · from ₹500", tone:"brand" },
                ].map(o => {
                  const active = f.design_source === o.v;
                  const activeCls = o.tone === "brand" ? "bg-[var(--pf-brand)] text-white border-[var(--pf-brand)]"
                                  : o.tone === "cyan" ? "bg-[var(--pf-cyan)] text-black border-[var(--pf-cyan)]"
                                  : "bg-[var(--pf-text)] text-white border-[var(--pf-text)]";
                  return (
                    <button key={o.v} type="button" data-testid={`design-source-${o.v}`}
                      onClick={()=>update("design_source", o.v)}
                      className={`text-left border-2 p-4 rounded-xl transition-all ${active ? activeCls : "border-[var(--pf-border)] bg-white hover:border-[var(--pf-text)]"}`}>
                      <o.i size={22} weight="duotone" />
                      <div className="font-bold mt-3">{o.t}</div>
                      <div className="text-xs mt-1 opacity-80">{o.d}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="pf-label block mb-2">Project title</label>
              <input className="pf-input" data-testid="brief-title" value={f.title} onChange={(e)=>update("title", e.target.value)} placeholder="e.g. Q1 Sales Team Cards" />
            </div>

            <div>
              <label className="pf-label block mb-3">Product type</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {PRODUCTS.map(p => (
                  <button key={p.v} type="button" data-testid={`product-${p.v}`}
                    onClick={()=>{ update("product_type", p.v); update("size", p.size); }}
                    className={`text-left border-2 rounded-xl p-4 transition-colors ${f.product_type===p.v?"bg-[var(--pf-text)] text-white border-[var(--pf-text)]":"border-[var(--pf-border)] bg-white hover:border-[var(--pf-text)]"}`}>
                    <div className="font-bold">{p.l}</div>
                    <div className={`text-xs mt-1 font-mono ${f.product_type===p.v?"opacity-70":"text-[var(--pf-text-2)]"}`}>{p.size}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-8">
              <div>
                <label className="pf-label block mb-2">Size / Format</label>
                <input className="pf-input" data-testid="brief-size" value={f.size} onChange={(e)=>update("size", e.target.value)} />
              </div>
              <div>
                <label className="pf-label block mb-2">Quantity</label>
                <input type="number" min="1" className="pf-input" data-testid="brief-quantity" value={f.quantity} onChange={(e)=>update("quantity", e.target.value)} />
              </div>
            </div>

            <div className="flex justify-end">
              <button type="button" onClick={()=>setStep(2)} disabled={!f.title} className="pf-btn-primary" data-testid="brief-next-1">Next →</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-8" data-testid="brief-step-2">
            <div>
              <label className="pf-label block mb-3">Industry</label>
              <div className="flex flex-wrap gap-2">
                {INDUSTRIES.map(i => (
                  <button key={i} type="button" data-testid={`industry-${i}`}
                    onClick={()=>{ update("industry", i); update("extra_fields", {}); }}
                    className={`px-4 py-2 border-2 rounded-lg text-sm font-semibold transition-colors ${f.industry===i?"bg-[var(--pf-text)] text-white border-[var(--pf-text)]":"border-[var(--pf-border)] bg-white hover:border-[var(--pf-text)]"}`}>{i}</button>
                ))}
              </div>
            </div>

            {industryFields.length > 0 && (
              <div className="border border-[var(--pf-border)] rounded-xl p-5 bg-white" data-testid="industry-fields">
                <div className="pf-label mb-4 text-[var(--pf-brand)]">{f.industry} details</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {industryFields.map(field => (
                    <div key={field.k} className={field.k === "amenities" || field.k === "signature_dishes" || field.k === "ingredients" ? "md:col-span-2" : ""}>
                      <label className="pf-label block mb-2">{field.l}</label>
                      <input className="pf-input" data-testid={`extra-${field.k}`}
                        value={f.extra_fields[field.k] || ""}
                        onChange={(e)=>updateExtra(field.k, e.target.value)}
                        placeholder={field.ph} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="pf-label block mb-3">Style</label>
              <div className="flex flex-wrap gap-2">
                {STYLES.map(s => (
                  <button key={s} type="button" data-testid={`style-${s}`}
                    onClick={()=>update("style", s)}
                    className={`px-4 py-2 border-2 rounded-lg text-sm font-semibold capitalize ${f.style===s?"bg-[var(--pf-text)] text-white border-[var(--pf-text)]":"border-[var(--pf-border)] bg-white hover:border-[var(--pf-text)]"}`}>{s}</button>
                ))}
              </div>
            </div>

            <div>
              <label className="pf-label block mb-3">Color scheme</label>
              <div className="flex flex-wrap gap-2">
                {COLORS.map(c => (
                  <button key={c} type="button" data-testid={`color-${c}`}
                    onClick={()=>update("color_scheme", c)}
                    className={`px-4 py-2 border-2 rounded-lg text-sm font-semibold capitalize ${f.color_scheme===c?"bg-[var(--pf-text)] text-white border-[var(--pf-text)]":"border-[var(--pf-border)] bg-white hover:border-[var(--pf-text)]"}`}>{c}</button>
                ))}
              </div>
            </div>

            <div className="flex justify-between">
              <button type="button" onClick={()=>setStep(1)} className="pf-btn-outline" data-testid="brief-back-2">← Back</button>
              <button type="button" onClick={()=>setStep(f.design_source === "template" ? 3 : 4)} className="pf-btn-primary" data-testid="brief-next-2">Next →</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-8" data-testid="brief-step-3">
            {f.design_source === "template" && (
              <div>
                <label className="pf-label block mb-3">Pick a template</label>
                {templates.length === 0 ? (
                  <div className="border border-dashed border-[var(--pf-border)] p-8 text-center text-[var(--pf-text-2)]">
                    Loading templates…
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="templates-grid">
                    {templates.map(t => {
                      const isSelected = f.template_id === t.id;
                      return (
                        <button key={t.id} type="button" onClick={()=>update("template_id", t.id)}
                          data-testid={`template-${t.id}`}
                          className={`text-left rounded-xl overflow-hidden border-2 transition-all ${isSelected ? "border-[var(--pf-brand)] shadow-lg scale-[1.02]" : "border-[var(--pf-border)] hover:border-[var(--pf-text)]"}`}>
                          <div className="aspect-[4/3] relative overflow-hidden" style={{ background: t.preview.bg }}>
                            <div className="absolute inset-3 border rounded" style={{ borderColor: t.preview.accent + '55' }}></div>
                            <div className="absolute bottom-3 left-3" style={{ color: t.preview.fg }}>
                              <div className="text-[7px] tracking-[0.25em] font-mono font-bold opacity-80">YOUR LOGO</div>
                              <div className="text-sm font-black tracking-tight">Brand Name</div>
                            </div>
                            <div className="absolute top-3 right-3 w-2 h-6" style={{ background: t.preview.accent }}></div>
                            {isSelected && (
                              <div className="absolute inset-0 bg-[var(--pf-brand)]/20 flex items-center justify-center">
                                <div className="bg-[var(--pf-brand)] text-white rounded-full p-2"><CheckCircle size={18} weight="fill"/></div>
                              </div>
                            )}
                          </div>
                          <div className="p-3 bg-white">
                            <div className="font-bold text-sm">{t.name}</div>
                            <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--pf-text-3)] mt-1">{t.style} · {t.industries[0]}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-between">
              <button type="button" onClick={()=>setStep(2)} className="pf-btn-outline" data-testid="brief-back-3">← Back</button>
              <button type="button" onClick={()=>setStep(4)} className="pf-btn-primary" data-testid="brief-next-3">Next →</button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-8" data-testid="brief-step-4">
            <div>
              <label className="pf-label block mb-3">
                Your logo <span className="text-[var(--pf-text-3)] normal-case tracking-normal font-normal">(optional · will be composited into the final design)</span>
              </label>
              {logoPreview ? (
                <div className="flex items-center gap-4 border border-[var(--pf-border)] rounded-xl p-4 bg-white" data-testid="logo-preview">
                  <img src={logoPreview} alt="logo" className="w-16 h-16 object-contain border border-[var(--pf-border)] rounded" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold">Logo uploaded ✓</div>
                    <div className="text-xs text-[var(--pf-text-2)]">Will be sent to the designer / template team.</div>
                  </div>
                  <button type="button" onClick={()=>{setLogoPreview(null); update("logo_base64", null);}} className="p-2 hover:bg-[var(--pf-surface-2)] rounded-lg" data-testid="logo-remove">
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <label className="flex items-center gap-3 border-2 border-dashed border-[var(--pf-border)] hover:border-[var(--pf-text)] rounded-xl p-6 cursor-pointer bg-white transition-colors" data-testid="logo-upload">
                  <div className="w-12 h-12 rounded-lg bg-[var(--pf-brand)]/10 text-[var(--pf-brand)] flex items-center justify-center">
                    <UploadSimple size={22} weight="duotone" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm">Upload logo</div>
                    <div className="text-xs text-[var(--pf-text-2)] mt-0.5">PNG, JPG, SVG up to 2MB · Transparent background recommended · You can also add or swap it later from the design editor</div>
                  </div>
                  <input type="file" accept="image/*" onChange={onLogoUpload} className="hidden" data-testid="logo-input" />
                </label>
              )}
            </div>

            <div>
              <label className="pf-label block mb-2">Describe your design vision</label>
              <textarea rows={6} className="pf-input" style={{borderBottom:"2px solid var(--pf-border)", padding:"0.75rem 0"}}
                data-testid="brief-description"
                value={f.description} onChange={(e)=>update("description", e.target.value)}
                placeholder="Include brand name, tagline, key messages, tone, references…" />
            </div>
            <div className="border border-[var(--pf-border)] rounded-xl p-5 bg-white">
              <div className="pf-label mb-3">Brief summary</div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-[var(--pf-text-2)]">Source:</span> {f.design_source.toUpperCase()}</div>
                <div><span className="text-[var(--pf-text-2)]">Product:</span> {f.product_type.replace('_',' ')}</div>
                <div><span className="text-[var(--pf-text-2)]">Industry:</span> {f.industry}</div>
                <div><span className="text-[var(--pf-text-2)]">Style:</span> {f.style}</div>
                <div><span className="text-[var(--pf-text-2)]">Colors:</span> {f.color_scheme}</div>
                <div><span className="text-[var(--pf-text-2)]">Qty:</span> {f.quantity}</div>
                {f.logo_base64 && <div className="col-span-2 text-[var(--pf-brand)] font-semibold">✓ Logo attached</div>}
                {f.template_id && <div className="col-span-2 text-[var(--pf-brand)] font-semibold">✓ Template: {templates.find(t=>t.id===f.template_id)?.name || f.template_id}</div>}
                {Object.keys(f.extra_fields).length > 0 && (
                  <div className="col-span-2 border-t border-[var(--pf-border)] pt-2 mt-1">
                    <div className="pf-label mb-2">{f.industry} details</div>
                    {Object.entries(f.extra_fields).filter(([_,v])=>v).map(([k,v]) => (
                      <div key={k} className="text-xs text-[var(--pf-text-2)]"><span className="font-semibold text-[var(--pf-text)]">{k.replace(/_/g,' ')}:</span> {v}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-between">
              <button type="button" onClick={()=>setStep(f.design_source === "template" ? 3 : 2)} className="pf-btn-outline" data-testid="brief-back-4">← Back</button>
              <button type="button" onClick={submit} disabled={loading || !f.description} className="pf-btn-primary" data-testid="brief-submit">
                {loading ? "Creating…" : "Create Brief →"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
